import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, UserCircle, Calendar, List, Table, AlertTriangle, MessageSquare, History, ChevronLeft, ChevronRight, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { wbsCodes, getCompanyFromEmail } from '../dummyData';
import { formatNumber } from '../utils/formatUtils';

export default function Timesheet({ activeUser, entries, onSave, showToast }) {
  const [activeView, setActiveView] = useState('grid'); // 'grid', 'calendar'
  const [weekOffset, setWeekOffset] = useState(0);

  // Base date: Monday, March 9, 2026
  const baseDate = new Date('2026-03-09T00:00:00');
  
  // Calculate current week's dates
  const weekStart = new Date(baseDate);
  weekStart.setDate(baseDate.getDate() + (weekOffset * 7));
  
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d.toISOString().split('T')[0];
  });
  
  const displayDates = dates.map(d => d.split('-')[2]);
  const currentMonth = weekStart.toISOString().split('-')[1];
  const currentYear = weekStart.getFullYear();

  // Calculate Week Number (Roughly for display)
  // March 9 2026 is Week 11
  const weekNumber = 11 + weekOffset;
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const [workingData, setWorkingData] = useState({}); // { [weekOffset]: rows }

  // Synchronize dynamic rows with actual saved entries OR existing drafts
  useEffect(() => {
    // Only load from global entries if we don't already have working data for this week
    if (!workingData[weekOffset]) {
      const entriesThisWeek = entries.filter(e => 
        e.userId === activeUser.id && 
        dates.includes(e.date)
      );

      let initialRows;
      if (entriesThisWeek.length > 0) {
        const grouped = entriesThisWeek.reduce((acc, entry) => {
          if (!acc[entry.wbsCode]) {
            acc[entry.wbsCode] = Array(7).fill(0);
          }
          const dayIdx = dates.indexOf(entry.date);
          if (dayIdx !== -1) {
            acc[entry.wbsCode][dayIdx] = entry.hours;
          }
          return acc;
        }, {});

        initialRows = Object.entries(grouped).map(([code, hours], idx) => ({
          id: `saved-${code}-${weekOffset}-${idx}`,
          wbsCode: code,
          hours
        }));
      } else {
        initialRows = [{ id: Date.now(), wbsCode: '', hours: Array(7).fill(0) }];
      }

      setWorkingData(prev => ({ ...prev, [weekOffset]: initialRows }));
    }
  }, [weekOffset, activeUser.id, entries]);

  // Use working data for the current week
  const rows = workingData[weekOffset] || [{ id: Date.now(), wbsCode: '', hours: Array(7).fill(0) }];
  
  const setRows = (newRows) => {
    setWorkingData(prev => ({ ...prev, [weekOffset]: newRows }));
  };

  // CRITICAL: Filter WBS based on active user's assigned scope
  const availableWBS = wbsCodes.filter(c => activeUser.roleScope === 'Management' || c.scope === activeUser.roleScope);

  const addRow = () => {
    setRows([...rows, { id: Date.now(), wbsCode: '', hours: Array(7).fill(0) }]);
  };

  const removeRow = (id) => {
    setRows(rows.filter(r => r.id !== id));
  };

  const updateHours = (rowId, dayIndex, val) => {
    const newRows = rows.map(r => {
      if (r.id === rowId) {
        const newHours = [...r.hours];
        const parsedVal = parseFloat(val);
        newHours[dayIndex] = isNaN(parsedVal) ? 0 : parsedVal;
        return { ...r, hours: newHours };
      }
      return r;
    });
    setRows(newRows);
  };

  const updateWBS = (rowId, code) => {
    setRows(rows.map(r => r.id === rowId ? { ...r, wbsCode: code } : r));
  };

  const populateStandardWeek = () => {
    // Smart populate: if no WBS is selected in first row, pick the first available one
    let targetWbs = rows[0]?.wbsCode;
    if (!targetWbs && availableWBS.length > 0) {
      targetWbs = availableWBS[0].code;
    }

    if (!targetWbs) {
      showToast('Please select a WBS or add a task first.', 'error');
      return;
    }

    const newRows = rows.map((r, i) => {
      if (i === 0) {
        return { ...r, wbsCode: targetWbs, hours: [7.5, 7.5, 7.5, 7.5, 7.5, 0, 0] };
      }
      return r;
    });
    setRows(newRows);
    showToast('Weekly standard (37.5h) populated', 'success');
  };

  const grandTotal = rows.reduce((acc, row) => acc + row.hours.reduce((sum, h) => sum + (h || 0), 0), 0);

  // Weekly Stats for the selected period
  const weeklyEntries = entries.filter(e => 
    e.userId === activeUser.id && 
    dates.includes(e.date)
  );
  
  const validatedHours = weeklyEntries
    .filter(e => e.status === 'Approved')
    .reduce((sum, e) => sum + e.hours, 0);
    
  const rejectedHours = weeklyEntries
    .filter(e => e.status === 'Rejected')
    .reduce((sum, e) => sum + e.hours, 0);

  const handleSave = () => {
    const newEntries = [];
    rows.forEach(row => {
      if (!row.wbsCode) return;
      row.hours.forEach((hrs, idx) => {
        if (hrs > 0) {
          const standardDate = dates[idx];
          newEntries.push({
            id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            userId: activeUser.id,
            wbsCode: row.wbsCode,
            date: standardDate,
            hours: hrs,
            status: 'Pending'
          });
        }
      });
    });

    if (newEntries.length === 0) {
      showToast('Please select a WBS and enter hours above 0.', 'error');
      return;
    }

    onSave(newEntries);
    // Clear draft for this week so it reloads from saved state
    setWorkingData(prev => {
      const newState = { ...prev };
      delete newState[weekOffset];
      return newState;
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">Weekly Timesheet</h2>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
              <div className="flex items-center gap-2 text-slate-500 font-medium text-sm">
                <Calendar className="w-4 h-4" />
                <span>{new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(dates[6]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase tracking-wider">W{weekNumber}</span>
              </div>
              {weekOffset === 0 && (
                <span className="w-fit px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-black uppercase tracking-widest animate-pulse">
                  Current Week
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 bg-white p-1 rounded-xl border border-slate-200 shadow-sm w-full md:w-auto overflow-x-auto no-scrollbar">
            <button 
              onClick={() => setActiveView('grid')}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${activeView === 'grid' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              <Table className="w-4 h-4" /> Grid
            </button>
            <button 
              onClick={() => setActiveView('calendar')}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${activeView === 'calendar' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              <Calendar className="w-4 h-4" /> Calendar
            </button>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 shadow-sm w-full md:w-auto">
             <UserCircle className="w-8 h-8 text-indigo-500 flex-shrink-0" />
             <div className="text-sm min-w-0">
               <p className="font-bold text-slate-800 leading-tight truncate">{activeUser.name}</p>
               <p className="text-slate-500 text-[10px] font-semibold truncate">{getCompanyFromEmail(activeUser.email)} Integration</p>
             </div>
          </div>
        </div>

        <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-indigo-900 font-medium">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
              Recording for <span className="font-bold">{activeUser.roleScope} Scope</span>
              <span className="hidden sm:inline text-indigo-200">|</span>
              Total: <span className="font-black text-indigo-700">{formatNumber(grandTotal)}h</span>
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs">
              <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div> Validated: <span className="text-emerald-600 font-bold">{formatNumber(validatedHours)}h</span></span>
              <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div> Rejected: <span className="text-rose-600 font-bold">{formatNumber(rejectedHours)}h</span></span>
            </div>
          </div>
          <div className="flex flex-col xs:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-indigo-100 shadow-sm w-full xs:w-auto justify-between sm:justify-start">
              <button 
                onClick={() => setWeekOffset(prev => prev - 1)}
                className="p-2 hover:bg-slate-50 rounded-md transition-all text-indigo-600 active:scale-90"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {weekOffset !== 0 ? (
                <button 
                  onClick={() => setWeekOffset(0)}
                  className="px-3 py-1 bg-white border border-indigo-200 rounded-md text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:bg-indigo-50 transition-colors shadow-sm whitespace-nowrap"
                >
                  Current
                </button>
              ) : (
                <span className="px-3 py-1 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Week {weekNumber}</span>
              )}
              <button 
                onClick={() => setWeekOffset(prev => prev + 1)}
                className="p-2 hover:bg-slate-50 rounded-md transition-all text-indigo-600 active:scale-90"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <button 
              onClick={populateStandardWeek}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-indigo-100 rounded-lg text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:border-indigo-300 hover:bg-indigo-50/50 transition-all shadow-sm group w-full xs:w-auto"
            >
              <CheckCircle2 className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
              Standard Week
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {activeView === 'grid' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr>
                  <th className="pb-4 font-bold text-slate-600 text-sm border-b border-slate-200 w-1/4 uppercase tracking-wider">Assigned WBS Task</th>
                  {daysOfWeek.map((day, i) => (
                    <th key={day} className={`pb-4 text-center border-b border-slate-200 ${i >= 5 ? 'bg-slate-50/50' : ''}`}>
                      <div className={`font-bold text-sm uppercase tracking-wider ${i >= 5 ? 'text-slate-400' : 'text-slate-600'}`}>{day}</div>
                      <div className={`text-xs font-medium mt-1 ${i >= 5 ? 'text-slate-300' : 'text-slate-400'}`}>{displayDates[i]}</div>
                    </th>
                  ))}
                  <th className="pb-4 text-center font-bold text-slate-600 text-sm border-b border-slate-200 uppercase tracking-wider">Total</th>
                  <th className="pb-4 border-b border-slate-200"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(row => {
                  const rowTotal = row.hours.reduce((sum, h) => sum + (h || 0), 0);
                  return (
                    <tr key={row.id} className="group hover:bg-slate-50 transition-colors">
                      <td className="py-4 pr-4">
                        <select 
                          value={row.wbsCode} 
                          onChange={(e) => updateWBS(row.id, e.target.value)}
                          className="w-full border border-slate-200 text-slate-700 bg-white rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm cursor-pointer"
                        >
                          <option value="">Select task...</option>
                          {availableWBS.map(wbs => (
                            <option key={wbs.id} value={wbs.code} className="font-medium text-slate-700">{wbs.code} - {wbs.description}</option>
                          ))}
                        </select>
                      </td>
                      {row.hours.map((hrs, idx) => (
                        <td key={idx} className={`py-4 px-1 ${idx >= 5 ? 'bg-slate-50/30' : ''}`}>
                          <input 
                            type="number" 
                            min="0" max="24" step="0.5"
                            value={hrs === 0 ? '' : hrs}
                            onChange={(e) => updateHours(row.id, idx, e.target.value)}
                            className={`w-full text-center border bg-white rounded-lg px-2 py-2 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm max-w-[60px] mx-auto block placeholder-slate-300 ${hrs > 7.5 ? 'border-red-300 text-red-600 bg-red-50' : 'border-slate-200 text-slate-700'} ${idx >= 5 ? 'opacity-70' : ''}`}
                            placeholder="0"
                          />
                        </td>
                      ))}
                      <td className="py-4 px-4 text-center">
                        <span className="font-bold text-slate-700 text-base">{formatNumber(rowTotal)}</span>
                      </td>
                      <td className="py-4 pl-4 text-right">
                        <button 
                          onClick={() => removeRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-red-50"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="pt-6">
                    <button 
                      onClick={addRow}
                      className="flex items-center gap-2 text-sm font-bold text-indigo-600 bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 transition-colors shadow-sm"
                    >
                      <Plus className="w-4 h-4" /> Add Task
                    </button>
                  </td>
                  <td colSpan="7" className="pt-6 text-right pr-4 text-slate-500 text-sm font-bold uppercase tracking-wider">
                    Grand Total
                  </td>
                  <td className="pt-6 text-center text-xl font-black text-indigo-700">
                    {formatNumber(grandTotal)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {activeView === 'calendar' && (
          <div className="space-y-12">
            {/* Current Week (Entry) */}
            <div>
              <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Current Week Entry
              </h3>
              <div className="grid grid-cols-7 gap-4 min-h-[150px]">
                {daysOfWeek.map((day, idx) => (
                  <div key={day} className="flex flex-col gap-2">
                    <div className={`p-2 rounded-lg border text-center transition-colors ${idx >= 5 ? 'bg-slate-100/50 border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
                      <p className={`text-[10px] font-black uppercase ${idx >= 5 ? 'text-slate-300' : 'text-slate-400'}`}>{day}</p>
                      <p className={`text-sm font-bold ${idx >= 5 ? 'text-slate-500 font-medium' : 'text-slate-800'}`}>{displayDates[idx]}</p>
                    </div>
                    <div className={`flex-1 border-2 border-dashed rounded-xl p-2 flex flex-col gap-2 transition-colors ${idx >= 5 ? 'bg-slate-50/50 border-slate-100' : 'bg-white border-slate-100'}`}>
                      {rows.filter(r => r.hours[idx] > 0).map(r => (
                        <div key={r.id} className={`p-2 rounded-lg text-[10px] border ${r.hours[idx] > 7.5 ? 'bg-red-50 border-red-100 text-red-700' : 'bg-indigo-50 border-indigo-100 text-indigo-700'}`}>
                          <p className="font-bold">{r.wbsCode || 'Task'}</p>
                          <p className="font-black">{r.hours[idx]}h</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Progress */}
            <div className="space-y-8 pt-4 border-t border-slate-100">
               <div>
                  <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-indigo-500" /> Monthly Progress Visualization
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">* Green indicates SAP-approved shifts</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* February 2026 */}
                    <div>
                      <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-4">February 2026</h4>
                      <div className="grid grid-cols-7 gap-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                          <div key={i} className="text-[9px] font-black text-slate-300 text-center mb-1">{d}</div>
                        ))}
                        {/* Empty cells for Feb start (Sunday start) */}
                        {Array.from({ length: 0 }).map((_, i) => <div key={i}></div>)}
                        {Array.from({ length: 28 }).map((_, i) => {
                          const day = i + 1;
                          const dateStr = `2026-02-${day.toString().padStart(2, '0')}`;
                          const dayEntries = initialTimesheetEntries.filter(e => e.date === dateStr && e.userId === activeUser.id);
                          const totalHrs = dayEntries.reduce((s, e) => s + e.hours, 0);
                          const isApproved = dayEntries.length > 0 && dayEntries.every(e => e.status === 'Approved');

                          const isWeekend = new Date(dateStr).getDay() === 0 || new Date(dateStr).getDay() === 6;

                          return (
                            <div key={i} className={`aspect-square border border-slate-100 rounded flex flex-col items-center justify-center relative group transition-colors ${isWeekend ? 'bg-slate-50/50' : 'bg-white'}`}>
                              <span className={`text-[9px] font-bold absolute top-0.5 left-1 ${isWeekend ? 'text-slate-300' : 'text-slate-400'}`}>{day}</span>
                              {totalHrs > 0 && (
                                <div className={`w-full h-full flex items-center justify-center text-[10px] font-black rounded transition-all ${isApproved ? 'bg-emerald-500 text-white shadow-sm' : 'bg-amber-100 text-amber-700'} ${isWeekend ? 'opacity-60' : ''}`}>
                                  {totalHrs}h
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* March 2026 */}
                    <div>
                      <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-4">March 2026</h4>
                      <div className="grid grid-cols-7 gap-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                          <div key={i} className="text-[9px] font-black text-slate-300 text-center mb-1">{d}</div>
                        ))}
                        {/* March 2026 starts on Sunday (0) */}
                        {Array.from({ length: 0 }).map((_, i) => <div key={i}></div>)}
                        {Array.from({ length: 31 }).map((_, i) => {
                          const day = i + 1;
                          const dateStr = `2026-03-${day.toString().padStart(2, '0')}`;
                          const dayEntries = initialTimesheetEntries.filter(e => e.date === dateStr && e.userId === activeUser.id);
                          const totalHrs = dayEntries.reduce((s, e) => s + e.hours, 0);
                          const isApproved = dayEntries.length > 0 && dayEntries.every(e => e.status === 'Approved');
                          
                          // Also check current active rows for this specific week (10-16)
                          let currentHrs = 0;
                          if (day >= 10 && day <= 16) {
                            const idx = day - 10;
                            currentHrs = rows.reduce((acc, r) => acc + r.hours[idx], 0);
                          }

                          const displayHrs = totalHrs + currentHrs;
                          const isWeekend = new Date(dateStr).getDay() === 0 || new Date(dateStr).getDay() === 6;

                          return (
                            <div key={i} className={`aspect-square border border-slate-100 rounded flex flex-col items-center justify-center relative group transition-colors ${isWeekend ? 'bg-slate-50/50' : 'bg-white'}`}>
                              <span className={`text-[9px] font-bold absolute top-0.5 left-1 ${isWeekend ? 'text-slate-300' : 'text-slate-400'}`}>{day}</span>
                              {displayHrs > 0 && (
                                <div className={`w-full h-full flex items-center justify-center text-[10px] font-black rounded transition-all ${isApproved ? 'bg-emerald-500 text-white shadow-sm' : 'bg-indigo-100 text-indigo-700'} ${isWeekend ? 'opacity-60' : ''}`}>
                                  {displayHrs}h
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 pb-6 pt-2 border-t border-slate-100 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5 text-indigo-500" />
              <h3 className="font-bold text-slate-800 uppercase tracking-wider text-sm">Notes for Approver</h3>
            </div>
            <textarea 
              placeholder="Add any comments regarding your weekly entries here..."
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none min-h-[120px] transition-all"
            />
          </div>
          {rows.some(r => r.hours.some(h => h > 7.5)) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-sm">Policy Alert</h3>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                <p className="text-amber-900 text-sm font-bold mb-2">Daily Limit Exceeded</p>
                <p className="text-amber-800 text-xs leading-relaxed font-medium">
                  One or more entries exceed the standard 7.5-hour daily limit. Please ensure manager pre-approval is documented for overtime or SAP might flag the entry.
                </p>
                <button className="mt-4 text-xs font-black text-amber-600 hover:text-amber-700 flex items-center gap-1 uppercase tracking-widest">
                  Learn More <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-slate-50 p-6 border-t border-slate-200 flex justify-between items-center">
        <div className="flex items-center gap-2 text-slate-400">
          <Clock className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-widest">Auto-saved at 14:18:10</span>
        </div>
        <button 
          onClick={handleSave}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-2xl font-black transition-all shadow-lg hover:shadow-indigo-200 active:scale-95 uppercase tracking-wider text-sm"
        >
          <Save className="w-4 h-4" /> Submit Weekly Timesheet
        </button>
      </div>

      {/* History Section */}
      <div className="mt-8 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-12">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <History className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-black text-slate-800 tracking-tight">Submission History</h3>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">Your recent activity & approvals</p>
            </div>
          </div>
          <button className="text-xs font-bold text-indigo-600 hover:text-indigo-700 uppercase tracking-widest">View Full Archive</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">Date Submitted</th>
                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">WBS Task</th>
                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">Hours</th>
                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">Status</th>
                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-widest">SAP Sync</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {weeklyEntries.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-slate-400 font-medium italic">
                    No verified history found for this period.
                  </td>
                </tr>
              ) : (
                weeklyEntries.map(entry => (
                <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6">
                    <p className="font-bold text-slate-700">{new Date(entry.date).toLocaleDateString()}</p>
                    <p className="text-[10px] text-slate-400 font-black">{new Date(entry.date).toLocaleTimeString()}</p>
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-black mr-2">{entry.wbsCode}</span>
                    <span className="text-sm font-bold text-slate-600">{wbsCodes.find(w => w.code === entry.wbsCode)?.description}</span>
                  </td>
                  <td className="py-4 px-6 font-black text-slate-700">{formatNumber(entry.hours)}h</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-black text-emerald-600 uppercase tracking-wider">{entry.status}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1.5 opacity-50">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Verified</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
              <tr className="hover:bg-slate-50 transition-colors text-amber-600">
                <td className="py-4 px-6">
                  <p className="font-bold">Today</p>
                  <p className="text-[10px] font-black uppercase tracking-widest">In Progress</p>
                </td>
                <td className="py-4 px-6">
                  <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded text-[10px] font-black mr-2">FIN-401.B</span>
                  <span className="text-sm font-bold italic">Mechanical/Electrical Fit-out</span>
                </td>
                <td className="py-4 px-6 font-black">8.0h</td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-black uppercase tracking-wider">Unsubmitted</span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-1.5">
                    <XCircle className="w-4 h-4 text-slate-300" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Waiting</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
